/* Collection grid (sections/collection-product-grid.liquid).
   fete-collection: filter sidebar/drawer, in-place filtering + sorting, Load more.
   fete-card: hover image on demand, quick add, colour swatches. */

if (!customElements.get('fete-collection')) {
  customElements.define('fete-collection', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.fete-plp');
      this.section.setAttribute('data-js', '');
      this.form = this.section.querySelector('.fete-controls');
      this.toggle = this.section.querySelector('[data-fete-filters-toggle]');
      this.isSidebar = this.section.classList.contains('fete-collection--filters-sidebar');
      this.desktop = window.matchMedia('(min-width: 990px)');
      // The whole section is swapped on every filter change; this tears down document-level listeners.
      this.abort = new AbortController();
      const signal = this.abort.signal;

      this.syncToggle();
      this.desktop.addEventListener('change', () => { this.close(); this.syncToggle(); }, { signal });

      if (this.toggle) this.toggle.addEventListener('click', () => this.onToggle());
      this.section.querySelectorAll('[data-fete-filters-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); }, { signal });

      // Sidebar on desktop applies each tick (and each price edit) straight away; the drawer waits for "Show results".
      this.section.querySelectorAll('.fete-facets input[type="checkbox"], .fete-facet__price input').forEach((input) => {
        input.addEventListener('change', () => {
          if (this.isSidebar && this.desktop.matches && this.form) this.form.requestSubmit();
        });
      });

      // Filters, sort, filter tags and stage-rail chips update the grid in place.
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.render(this.formUrl());
        });
      }
      this.section.addEventListener('click', (e) => {
        const link = e.target.closest('.fete-toolbar__tag, .fete-toolbar__clear, .fete-facets__clear, .fete-empty__link');
        if (link) { e.preventDefault(); this.render(link.getAttribute('href')); }
      });
      document.addEventListener('click', (e) => {
        const chip = e.target.closest('.stage-rail a.stage-pill');
        if (chip && !e.metaKey && !e.ctrlKey) { e.preventDefault(); this.render(chip.getAttribute('href')); }
      }, { signal });
      window.addEventListener('popstate', () => this.render(window.location.href, false), { signal });

      // "Show all N" for long facet lists.
      this.section.querySelectorAll('[data-fete-facet-more]').forEach((btn) => {
        btn.hidden = false;
        btn.addEventListener('click', () => {
          btn.closest('.fete-facet').setAttribute('data-expanded', '');
          btn.remove();
        });
      });

      const more = this.section.querySelector('[data-fete-loadmore]');
      if (more) more.addEventListener('click', (e) => this.loadMore(e, more));
    }
    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }
    syncToggle() {
      if (!this.toggle) return;
      const sidebarVisible = this.isSidebar && this.desktop.matches && !this.section.hasAttribute('data-filters-hidden');
      this.toggle.setAttribute('aria-expanded', sidebarVisible ? 'true' : 'false');
    }
    onToggle() {
      if (this.isSidebar && this.desktop.matches) {
        this.section.toggleAttribute('data-filters-hidden');
        this.syncToggle();
      } else {
        this.open();
      }
    }
    open() {
      this.section.setAttribute('data-filters-open', '');
      document.documentElement.style.overflow = 'hidden';
      this.toggle.setAttribute('aria-expanded', 'true');
      const close = this.section.querySelector('.fete-facets__close');
      if (close) close.focus();
    }
    close() {
      if (!this.section.hasAttribute('data-filters-open')) return;
      this.section.removeAttribute('data-filters-open');
      document.documentElement.style.overflow = '';
      this.syncToggle();
      if (this.toggle) this.toggle.focus();
    }
    // Current filters + sort as a clean URL (empty price boxes are left out).
    formUrl() {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form)) {
        if (value !== '') params.append(key, value);
      }
      const query = params.toString();
      return window.location.pathname + (query ? '?' + query : '');
    }
    // Re-render this grid and the stage rail for a filter/sort URL via the
    // Section Rendering API, instead of reloading the whole page.
    async render(href, push = true) {
      const url = new URL(href, window.location.href);
      const gridId = this.section.dataset.sectionId;
      const ids = [gridId];
      document.querySelectorAll('.shopify-section').forEach((wrap) => {
        if (wrap.querySelector('.stage-rail')) ids.push(wrap.id.replace('shopify-section-', ''));
      });
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('sections', ids.join(','));

      const wasHidden = this.section.hasAttribute('data-filters-hidden');
      const focused = document.activeElement;
      const focusKey = focused && focused.name ? [focused.name, focused.value] : null;
      this.section.setAttribute('aria-busy', 'true');

      try {
        // No Accept: application/json header: with it Shopify returns the collection's JSON, not the sections.
        const res = await fetch(fetchUrl.toString());
        if (!res.ok) throw new Error('render failed');
        const sections = await res.json();
        if (!sections[gridId]) throw new Error('missing grid');

        document.documentElement.style.overflow = '';
        for (const id of ids) {
          const wrap = document.getElementById('shopify-section-' + id);
          if (!wrap || !sections[id]) continue;
          const doc = new DOMParser().parseFromString(sections[id], 'text/html');
          const fresh = doc.getElementById('shopify-section-' + id);
          wrap.innerHTML = fresh ? fresh.innerHTML : sections[id];
        }

        const section = document.querySelector('[data-section-id="' + gridId + '"]');
        if (section) {
          if (wasHidden) section.setAttribute('data-filters-hidden', '');
          const controller = section.querySelector('fete-collection');
          if (controller && controller.syncToggle) controller.syncToggle();
          if (focusKey) {
            const match = Array.from(section.querySelectorAll('[name]')).find((el) => el.name === focusKey[0] && el.value === focusKey[1]);
            if (match) match.focus({ preventScroll: true });
          }
          // Bring the top of the grid back into view if the shopper had scrolled past it.
          const top = section.getBoundingClientRect().top;
          if (top < 0) window.scrollBy({ top: top, behavior: 'smooth' });
        }

        if (push) window.history.pushState({}, '', url.pathname + url.search);
        document.dispatchEvent(new CustomEvent('fete:cards-added'));
      } catch (_) {
        window.location.href = url.toString(); // fall back to a normal page load
      }
    }
    async loadMore(e, btn) {
      e.preventDefault();
      if (btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(btn.getAttribute('href'), window.location.href);
        url.searchParams.set('section_id', this.section.dataset.sectionId);
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error('load failed');
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const grid = this.section.querySelector('[data-fete-grid]');
        doc.querySelectorAll('[data-fete-grid] > .fete-card').forEach((card) => grid.appendChild(document.adoptNode(card)));

        const shown = this.section.querySelector('[data-fete-shown]');
        const nextShown = doc.querySelector('[data-fete-shown]');
        if (shown && nextShown) shown.textContent = nextShown.textContent;
        const nextBtn = doc.querySelector('[data-fete-loadmore]');
        if (nextBtn) { btn.href = nextBtn.getAttribute('href'); } else { btn.remove(); }

        // Let the wishlist script reveal hearts on the new cards.
        document.dispatchEvent(new CustomEvent('fete:cards-added'));
      } catch (_) {
        window.location.href = btn.href; // fall back to the plain next-page link
        return;
      } finally {
        btn.removeAttribute('aria-busy');
      }
    }
  });
}

if (!customElements.get('fete-card')) {
  customElements.define('fete-card', class extends HTMLElement {
    connectedCallback() {
      // ---- AJAX quick add (all cards): stay on the PLP instead of
      //      navigating to /cart. Falls back to native submit on error. ----
      this.addForm = this.querySelector('form[action*="/cart/add"]');
      if (this.addForm) {
        this.addForm.addEventListener('submit', (e) => this.quickAdd(e));
      }

      // ---- Hover image: fetched on the first hover only (never on touch screens) ----
      const hoverImg = this.querySelector('.fete-card__img--hover[data-src]');
      if (hoverImg && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.querySelector('.fete-card__media').addEventListener('pointerenter', () => {
          hoverImg.addEventListener('load', () => hoverImg.classList.add('is-loaded'), { once: true });
          hoverImg.srcset = hoverImg.dataset.srcset;
          hoverImg.src = hoverImg.dataset.src;
        }, { once: true });
      }

      // ---- Swatches (cards with a single Color option only) ----
      const dataEl = this.querySelector('.fete-card__data');
      if (!dataEl) return; // no swatches on this card
      try { this.variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
      this.img = this.querySelector('.fete-card__img:not(.fete-card__img--hover)');
      this.priceEl = this.querySelector('.fete-card__price-current');
      this.addId = this.querySelector('.fete-card__add-id');
      this.addBtn = this.querySelector('.fete-card__add');
      this.swatches = Array.from(this.querySelectorAll('.fete-card__swatch'));
      this.soBadge = this.querySelector('[data-variant-soldout]');
      this.swatches.forEach((sw) => {
        sw.addEventListener('click', () => this.select(sw.dataset.value));
        const v = this.variants.find((x) => x.value === sw.dataset.value);
        if (v && !v.available) sw.classList.add('fete-card__swatch--so');
      });
      const sel = this.getAttribute('data-selected');
      const init = this.swatches.find((s) => s.dataset.value === sel) || this.swatches[0];
      if (init) init.setAttribute('aria-pressed', 'true');
    }
    async quickAdd(e) {
      e.preventDefault();
      const btn = this.addForm.querySelector('.fete-card__add');
      if (btn) btn.disabled = true;
      try {
        const res = await fetch(this.addForm.action + '.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.addForm),
        });
        if (!res.ok) throw new Error('add failed');
        const cart = await (await fetch('/cart.js', { headers: { 'Accept': 'application/json' } })).json();

        // Update any visible cart count bubbles (common patterns + Horizon).
        document.querySelectorAll('[data-cart-count], .cart-count-bubble span, cart-icon [aria-hidden="true"]').forEach((el) => {
          el.textContent = cart.item_count;
        });

        // Notify the theme via its own events. Horizon's cart-drawer-component
        // listens on document for CartAddEvent.eventName and auto-opens only
        // when it carries the auto-open attribute (a theme setting), so
        // dispatching the real event lets the theme decide drawer behavior.
        // The event classes are read at runtime through Horizon's import map.
        const detail = { resource: null, sourceId: this.id || 'fete-card', data: { source: 'quick-add', itemCount: cart.item_count, cart: cart } };
        let usedThemeEvents = false;
        try {
          const events = await import('@theme/events');
          for (const name of ['CartAddEvent', 'CartUpdateEvent']) {
            if (events[name] && events[name].eventName) {
              document.dispatchEvent(new CustomEvent(events[name].eventName, { bubbles: true, detail }));
              usedThemeEvents = true;
            }
          }
        } catch (_) { /* no import map in this context; fall through */ }

        if (!usedThemeEvents) {
          // Generic fallbacks for non-Horizon contexts.
          document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
          const drawer = document.querySelector('cart-drawer-component, cart-drawer');
          if (drawer) {
            try { (drawer.open || drawer.showDialog || drawer.show || function(){}).call(drawer); } catch (_) {}
          }
        }

        this.flashAdded(btn);
      } catch (_) {
        // Graceful fallback: native submit (navigates to cart, but never strands the user).
        this.addForm.submit();
        return;
      } finally {
        if (btn) btn.disabled = false;
      }
    }
    flashAdded(btn) {
      if (!btn || btn.dataset.flashing) return;
      btn.dataset.flashing = '1';
      const original = btn.textContent;
      btn.textContent = 'Added';
      btn.classList.add('fete-card__quick--added');
      setTimeout(() => { btn.textContent = original; btn.classList.remove('fete-card__quick--added'); delete btn.dataset.flashing; }, 1600);
    }
    select(value) {
      const v = this.variants.find((x) => x.value === value);
      if (!v) return;
      if (v.img && this.img) { this.img.src = v.img; this.img.removeAttribute('srcset'); }
      if (this.priceEl) this.priceEl.textContent = v.price;
      if (this.addId) this.addId.value = v.id;
      if (this.addBtn) this.addBtn.disabled = !v.available;
      if (this.soBadge) this.soBadge.hidden = v.available;
      this.swatches.forEach((s) => s.setAttribute('aria-pressed', s.dataset.value === value ? 'true' : 'false'));
    }
  });
}
